import { Fragment } from 'react'
import { isNil } from 'lodash'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import { DATE_FORMATS } from '@/utils/dateHelpers'
import { User } from '@/reducers/auth/userReducer'
import { useReselector } from '@/utils/sharedHooks'
import { selectAdminOrUserGroupedFinancialAccountOptions } from '@/selectors/financeSelectors'
import { TaxFilingUser } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilings.slice'
import { GroupedDropdown } from '@/shared/GroupedDropdown'
import { useIsDeviceWidth, DeviceWidthCombo } from '@/utils/deviceWidthHelpers'
import { DatePicker } from '@/base'
import { TypographyStyles } from '@/styles'
import { Icon, Link, Separator, Text } from '@/ui'

export interface StatementData {
  statementMonth: string
  financialAccountIds: number[]
}

export const StatementUploadSection = ({
  filesToUpload,
  setFilesToUpload,
  statementData,
  setStatementData,
  user,
  useFilteredAccountOptions,
}: {
  filesToUpload: File[]
  setFilesToUpload: (files: File[]) => void
  statementData: StatementData[]
  setStatementData: (statementData: StatementData[]) => void
  user: User | TaxFilingUser | null
  useFilteredAccountOptions?: boolean
}) => {
  const isMobileOrTablet = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)

  const financialAccountOptions = useReselector(
    selectAdminOrUserGroupedFinancialAccountOptions,
    user?.id
  )

  const filteredFinancialAccountOptions = financialAccountOptions.filter(
    (option) =>
      isNil(option.statementPermissions) ||
      option.statementPermissions === 'plaid_statement_disabled'
  )

  const handleRemove = (index: number) => {
    setFilesToUpload([
      ...filesToUpload.slice(0, index),
      ...filesToUpload.slice(index + 1),
    ])
    setStatementData([
      ...statementData.slice(0, index),
      ...statementData.slice(index + 1),
    ])
  }

  const handleUpdate = <T,>(index: number, type: string, value: T) => {
    const updatedStatementData = [...statementData]
    updatedStatementData[index] = {
      ...updatedStatementData[index],
      [type]: value,
    }
    setStatementData(updatedStatementData)
  }

  return (
    <>
      <Separator />

      <div className="w-full">
        <Text variant={TypographyStyles.H3}> Uploaded </Text>

        {isMobileOrTablet && (
          <div className="p-4 space-y-4">
            {filesToUpload.map((file, index) => (
              <Fragment key={file.name}>
                <div className="flex justify-between items-center">
                  <div className="w-7/8">
                    <Link href={URL.createObjectURL(file)} download={file.name}>
                      {file.name}
                    </Link>
                  </div>

                  <Icon
                    className="ml-2 cursor-pointer"
                    icon={regular('trash-can')}
                    onClick={() => handleRemove(index)}
                  />
                </div>

                <DatePicker
                  placeholder="Select month"
                  value={statementData[index]?.statementMonth}
                  showMonthYearPicker
                  dateFormat={DATE_FORMATS.YEAR_MONTH}
                  placeholderText="MM/YYYY"
                  className="w-full"
                  onChange={(date) =>
                    handleUpdate(index, 'statementMonth', date)
                  }
                />

                <GroupedDropdown
                  placeholder="Select account"
                  value={statementData[index]?.financialAccountIds || []}
                  className="w-full"
                  allowOneParent
                  height={50}
                  options={
                    useFilteredAccountOptions
                      ? filteredFinancialAccountOptions
                      : financialAccountOptions
                  }
                  onChange={(value) =>
                    handleUpdate(index, 'financialAccountIds', value)
                  }
                />
              </Fragment>
            ))}
          </div>
        )}

        {!isMobileOrTablet &&
          filesToUpload.map((file, index) => (
            <div className="flex items-center gap-2 mb-4" key={file.name}>
              <Link
                className="w-30 whitespace-nowrap overflow-hidden text-ellipsis"
                href={URL.createObjectURL(file)}
                download={file.name}
              >
                {file.name}
              </Link>

              <DatePicker
                value={statementData[index]?.statementMonth}
                onChange={(date) => handleUpdate(index, 'statementMonth', date)}
                showMonthYearPicker
                dateFormat={DATE_FORMATS.YEAR_MONTH}
                placeholderText="MM/YYYY"
                placeholder="Select month"
                maxDate={new Date()}
                className="w-[160px]"
              />

              <GroupedDropdown
                className="w-[260px]"
                placeholder="Select account"
                value={statementData[index]?.financialAccountIds || []}
                allowOneParent
                height={50}
                options={
                  useFilteredAccountOptions
                    ? filteredFinancialAccountOptions
                    : financialAccountOptions
                }
                onChange={(value) =>
                  handleUpdate(index, 'financialAccountIds', value)
                }
              />

              <Icon
                className="ml-2 cursor-pointer"
                icon={regular('trash-can')}
                onClick={() => handleRemove(index)}
              />
            </div>
          ))}
      </div>
    </>
  )
}
